import { SerializedError } from "@reduxjs/toolkit";
import { Location } from "history";
import jwtDecode from 'jwt-decode';
import moment from "moment";
import pluralize from "pluralize";
import cookieService from "../../features/auth/services/cookie.service";
import { Plan } from "../../features/planUpgrade/models/Plan.interface";

export type Unit = 'hour' | 'hours' | 'day' | 'days' | 'week' | 'weeks' | 'month' | 'months' | 'year' | 'years';

export function extractDuration(duration: string): { quantity: number, unit: Unit } {
    const regexpDuration = /^([0-9]+)\s?(hour|hours|day|days|week|weeks|month|months|year|years)$/;
    try {
        const [, quantity, unit]: RegExpMatchArray = duration.match(regexpDuration) as RegExpMatchArray;
        const singularizedUnit = pluralize.singular(unit);
        return { quantity: parseInt(quantity), unit: singularizedUnit } as { quantity: number, unit: Unit };
    } catch (error: unknown) {
        throw new Error("Invalid duration given " + duration);
    }
}

export function removeEmptyFields(data: any) {
    Object.keys(data).forEach(key => {
        if (data[key] === '' || data[key] == null) {
            delete data[key];
        }
    });
    return data;
}

export function formatDaysLeft(targetDate: string) {
    const displayEndate = moment(targetDate).format('MMMM Do YYYY, h:mm:ss a')
    const now = moment();
    const daysToEnd = moment.duration(moment(targetDate).diff(now)).asDays();
    let displayEndDateCount = `Will expire on ${displayEndate}`;
    if (daysToEnd < 31) {
        displayEndDateCount = `(${Math.floor(daysToEnd)} days left)`;
    }
    if (daysToEnd < 0) {
        return `(Ended ${moment(targetDate).fromNow()})`;
    }
    if (daysToEnd < 1) {
        return `(Ending ${moment(targetDate).fromNow()})`;
    }
    if (daysToEnd < 0) {
        return "";
    }
    return displayEndDateCount;
}

export function countdown(value: string) {
    moment.relativeTimeThreshold('ss', 3);
    moment.relativeTimeThreshold('s', 40);
    moment.relativeTimeThreshold('m', 40);
    moment.relativeTimeThreshold('h', 20);
    moment.relativeTimeThreshold('d', 28);
    moment.relativeTimeThreshold('M', 10);
    if (value) {
        return moment(value).locale('en').fromNow();
    } else {
        return '';
    }
}

interface DecodedToken {
    account: string;
    id: string;
    region: string;
    username?: string;
    exp: number;
    iat: number;
    ip: string;
}

export function getDecodedToken(): DecodedToken {
    const token = cookieService.get(process.env.REACT_APP_TOKEN_NAME as string);
    const decodedToken: any = jwtDecode(token) as DecodedToken;
    return decodedToken;
}

export function isNumber(value: any) {
    return typeof value === 'number';
}

export function isUndefined(value: any) {
    return typeof value === 'undefined';
}

export function isNumberFinite(value: any) {
    return isNumber(value) && isFinite(value);
}

export function applyPrecision(num: number, precision: number) {
    if (precision <= 0) {
        return Math.round(num);
    }
    const tho = 10 ** precision;
    return Math.round(num * tho) / tho;
}


export const humanizeBytes = (value: number | null, precision?: number): string | null => {
    const dictionary: Array<{ max: number; type: string }> = [
        { max: 1000, type: 'B' },
        { max: 1000000, type: 'KB' },
        { max: 1000000000, type: 'MB' },
        { max: 1000000000000, type: 'GB' },
        { max: 1000000000000000, type: 'TB' },
        { max: Infinity, type: '-' }, // TO FIX don't know what to set here
    ];

    if (value === null || Number.isNaN(value) || !isNumberFinite(value)) {
        return null;
    }

    if (!precision) {
        precision = 2
    }

    const format = dictionary.find(d => value < d.max) || dictionary[dictionary.length - 1];
    const calc = value / (format.max / 1000);
    const num = isUndefined(precision) ? calc : applyPrecision(calc, precision);
    return `${num} ${format.type}`;
}

export const computeAutoPay = (duration: string) => {
    const { quantity, unit } = extractDuration(duration)
    const endDate = moment().add(quantity, unit);
    return moment(endDate).format('MMMM Do YYYY');
}

export const sortPlansByData = (plans: Plan[]): Plan[] => {
    return plans.sort((a, b) => (a.limits.services.transfer.dataInOut.max - b.limits.services.transfer.dataInOut.max))
}

export const formatDate = (dateIso: string): string => {
    return moment(dateIso).format('MMMM Do, YYYY - hh:mm:ss a')
}

export type TimePeriod = '7_DAYS' | '4_WEEKS' | '3_MONTHS' | '12_MONTHS' | 'LAST_YEAR' | 'YEAR_TO_DATE';
export type TimeUnit = 'day' | 'week' | 'month' | 'year';

export function buildDates(period: TimePeriod) {
    let startDate;
    let endDate;
    switch (period) {
        case '7_DAYS':
            startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
            return { startDate, endDate };
        case '4_WEEKS':
            startDate = moment().startOf('isoWeek').subtract(3, 'weeks').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
            return { startDate, endDate };
        case '3_MONTHS':
            startDate = moment().startOf('month').subtract(2, 'months').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
            return { startDate, endDate };
        case '12_MONTHS':
            startDate = moment().startOf('month').subtract(11, 'months').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
            return { startDate, endDate };
        case 'LAST_YEAR':
            startDate = moment().startOf('year').subtract(1, 'year').format('YYYY-MM-DD');
            endDate = moment().startOf('year').subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
            return { startDate, endDate };
        case 'YEAR_TO_DATE':
            startDate = moment().startOf('year').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
            return { startDate, endDate };
        default:
            startDate = moment().subtract(7, 'd').format('YYYY-MM-DD');
            endDate = moment().format('YYYY-MM-DD');
            return { startDate, endDate };
    }
}

export function isApiPlan({ name }: { name: string }) {
    if (!name) {
        throw new Error('Invalid plan name');
    }
    return name.toLowerCase().includes('api');
}

export function convertToGB(value: number): number {
    return Number((value / (1000 * 1000 * 1000)).toFixed(2));
}

export function capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getProtocolHostnameAndPort() {
    const url = window.location.href;
    const arr = url.split('/');
    return arr[0] + '//' + arr[2];
}

export function buildScitylanaEvent(location: Location) {
    return {
        event: 'pageView',
        url: getProtocolHostnameAndPort() + location.pathname + location.search,
        referer: document.referrer,
        date: new Date().toISOString()
    };
}